import React, { useState, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';
import FileUploadModule from '../../modules/file-upload/FileUploadModule.jsx';
import { ModuleContextProvider } from '../../modules/context/ModuleContextProvider.jsx';

/**
 * Knowledge Base file upload modal component with bulk upload support
 */
const KnowledgeBaseUploadModal = ({
  isOpen,
  onClose,
  onUploadComplete
}) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();
  const fileInputRef = useRef(null);
  
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    assignToUser: '',
    autoAssociate: false
  });

  /**
   * Handle form input changes
   */
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (uploading) {
      if (!confirm('Upload is in progress. Are you sure you want to close?')) {
        return;
      }
    }
    
    setFormData({
      description: '',
      assignToUser: '',
      autoAssociate: false
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg border w-full max-w-2xl max-h-[90vh] overflow-hidden ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex justify-between items-center ${
          theme === 'dark' 
            ? 'bg-gray-900 border-gray-700' 
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div>
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Upload Knowledge Base Files</h3>
            <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Upload knowledge base files for parrot training
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={uploading}
            className={`transition-colors disabled:opacity-50 ${
              theme === 'dark' 
                ? 'text-gray-400 hover:text-white' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            ✕
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <ModuleContextProvider>
            <div className="space-y-6">
              {/* Upload Options */}
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={2}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                    placeholder="Add a description for these files..."
                    disabled={uploading}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Assign to User (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.assignToUser}
                    onChange={(e) => handleInputChange('assignToUser', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                    placeholder="User ID or email..."
                    disabled={uploading}
                  />
                  <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Leave empty to create as admin-owned files
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="autoAssociate"
                    checked={formData.autoAssociate}
                    onChange={(e) => handleInputChange('autoAssociate', e.target.checked)}
                    className={`mr-2 rounded text-teal-600 focus:ring-teal-500 ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600'
                        : 'bg-white border-gray-300'
                    }`}
                    disabled={uploading}
                  />
                  <label htmlFor="autoAssociate" className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Auto-associate with user's parrots
                  </label>
                </div>
              </div>

              {/* File Upload Module */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select Files
                </label>
                <FileUploadModule
                  config={{
                    context: 'admin-kb',
                    securityLevel: 'admin',
                    allowedTypes: ['txt', 'md', 'pdf', 'csv', 'json', 'html'],
                    maxFileSize: 10 * 1024 * 1024, // 10MB
                    multiple: true,
                    showProgress: true,
                    concurrent: true,
                    maxConcurrentUploads: 3,
                    uploadEndpoint: '/api/admin/knowledge-base',
                    triggerSync: formData.autoAssociate,
                    metadata: {
                      description: formData.description,
                      assignToUser: formData.assignToUser,
                      autoAssociate: formData.autoAssociate
                    },
                    onUploadComplete: (results) => {
                      console.log('Admin files uploaded:', results);
                      setUploading(false);
                      
                      const successful = results.successful?.length || 0;
                      const failed = results.failed?.length || 0;

                      if (successful > 0) {
                        showToast(`Successfully uploaded ${successful} file${successful !== 1 ? 's' : ''}`, 'success');
                        if (onUploadComplete) {
                          onUploadComplete();
                        }
                      }

                      if (failed > 0) {
                        showToast(`Failed to upload ${failed} file${failed !== 1 ? 's' : ''}`, 'error');
                      }

                      // Close modal if all uploads were successful
                      if (failed === 0) {
                        handleClose();
                      }
                    }
                  }}
                  onEvent={(event) => {
                    console.log('Admin FileUpload event:', event);
                    if (event.type === 'USER_ACTION' && event.payload.action === 'file_upload_started') {
                      setUploading(true);
                    }
                  }}
                />
              </div>
            </div>
          </ModuleContextProvider>
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t flex justify-end ${
          theme === 'dark' 
            ? 'bg-gray-900 border-gray-700' 
            : 'bg-gray-50 border-gray-200'
        }`}>
          <button
            onClick={handleClose}
            disabled={uploading}
            className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
              theme === 'dark'
                ? 'bg-gray-600 text-white hover:bg-gray-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {uploading ? 'Uploading...' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseUploadModal;
